// Painel — KPIs + próximas + em curso

import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import {
  AlertCircle,
  Calendar,
  ClipboardList,
  Clock,
  Database,
  Home,
  Loader2,
  Users
} from "lucide-react";
import { api, ApiError, type TeacherHome } from "../api";
import { friendlyError } from "@app/ui/lib/friendly-error";

const data = signal<TeacherHome | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const err = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  err.value = null;
  try {
    data.value = await api.home();
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    err.value = friendlyError(e);
    phase.value = "error";
  }
}

export function PainelView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading") {
    return (
      <div class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {err.value}
      </div>
    );
  }
  const d = data.value!;
  return (
    <div class="space-y-6">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Home className="size-6 text-primary" />
          Painel
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Visão geral das suas atividades como professor.
        </p>
      </header>

      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Meus bancos" value={d.kpis.banksAtivos} sub={`${d.kpis.banksArquivados} arquivados`} icon={Database} accent="violet" />
        <Kpi label="Turmas" value={d.kpis.turmas} icon={Users} accent="emerald" />
        <Kpi label="Provas futuras" value={d.kpis.provasFuturas} sub={`${d.kpis.provasTotal} no total`} icon={Calendar} accent="sky" />
        <Kpi label="Em curso agora" value={d.kpis.provasLive} icon={ClipboardList} accent={d.kpis.provasLive > 0 ? "amber" : "muted"} />
      </div>

      <section class="space-y-2 rounded-md border border-border bg-card/40 p-4">
        <h2 class="flex items-center gap-2 text-sm font-bold">
          <Calendar className="size-4 text-primary" />
          Próximas provas
        </h2>
        {d.upcoming.length === 0 ? (
          <p class="text-xs italic text-muted-foreground">Nada agendado.</p>
        ) : (
          <ul class="space-y-1">
            {d.upcoming.map((e) => (
              <li
                key={e.id}
                class="flex items-center gap-3 rounded-md border border-border bg-background/50 px-3 py-2 text-xs"
              >
                <div class="min-w-0 flex-1">
                  <div class="truncate font-semibold">{e.title}</div>
                  <div class="font-mono text-[10px] text-muted-foreground">
                    {new Date(e.startsAt).toLocaleString("pt-BR")}
                    {e.endsAt && ` → ${new Date(e.endsAt).toLocaleString("pt-BR")}`}
                  </div>
                </div>
                <span class="rounded bg-accent px-1.5 py-0.5 text-[10px] font-bold uppercase">
                  {e.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section class="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-4">
        <h2 class="flex items-center gap-2 text-sm font-bold text-amber-700 dark:text-amber-300">
          <Clock className="size-4" />
          Em curso agora
        </h2>
        {d.live.length === 0 ? (
          <p class="text-xs italic text-muted-foreground">Nenhuma prova rodando.</p>
        ) : (
          <ul class="space-y-1">
            {d.live.map((e) => (
              <li
                key={e.id}
                class="flex items-center gap-3 rounded-md border border-amber-500/40 bg-background/50 px-3 py-2 text-xs"
              >
                <div class="min-w-0 flex-1">
                  <div class="truncate font-semibold">{e.title}</div>
                  <div class="font-mono text-[10px] text-muted-foreground">
                    encerra {new Date(e.endsAt).toLocaleString("pt-BR")}
                  </div>
                </div>
                <span class="inline-flex items-center gap-1 rounded bg-amber-500/25 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:text-amber-300">
                  <span class="size-1.5 animate-pulse rounded-full bg-amber-500" />
                  EM CURSO
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  icon: Icon,
  accent
}: {
  label: string;
  value: number;
  sub?: string;
  icon: typeof Database;
  accent: "violet" | "emerald" | "sky" | "rose" | "amber" | "muted";
}) {
  const colorBg =
    accent === "violet"
      ? "bg-violet-500/10 text-violet-700 dark:text-violet-300"
      : accent === "emerald"
        ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
        : accent === "sky"
          ? "bg-sky-500/10 text-sky-700 dark:text-sky-300"
          : accent === "amber"
            ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
            : accent === "rose"
              ? "bg-rose-500/10 text-rose-700 dark:text-rose-300"
            : "bg-muted text-muted-foreground";
  return (
    <div class={`flex items-center gap-3 rounded-md border border-border px-4 py-3 ${colorBg}`}>
      <Icon className="size-5 shrink-0" />
      <div class="min-w-0">
        <div class="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
        <div class="text-2xl font-bold tabular-nums text-foreground">{value}</div>
        {sub && <div class="text-[10px] text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
}
